import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import { IconClockPause } from '@tabler/icons-react';
import { ChevronRight, Loader2, Route } from 'lucide-react';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import ProcessConfigSkeleton from '../ConfigStatusView2Skeleton';
import '../ConfigStatusView2.css';

function Pcs2Subblock({ title, desc, children, hideHead = false }) {
  return (
    <div className="pcs2-subblock">
      {!hideHead && (title || desc) ? (
        <div className="pcs2-subhead">
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Pcs2Section({ icon: Icon, title, desc, children }) {
  return (
    <section className="pcs2-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        <p className="pcs2-section-desc">{desc}</p>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2ListItem({ icon: Icon, label, id, value, onChange, disabled }) {
  return (
    <div className={`pcs2-list-item${disabled ? ' pcs2-list-item--disabled' : ''}`}>
      <span className="pcs2-list-icon">
        <Icon size={12} stroke={1.75} aria-hidden />
      </span>
      <span className="pcs2-list-label">{label}</span>
      <input
        type="text"
        id={id}
        className="pcs2-list-pin"
        maxLength={2}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-label={label}
      />
      <ChevronRight className="pcs2-list-chev" aria-hidden />
    </div>
  );
}

function Pcs2SettingRow({ title, desc, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? <span className="pcs2-setting-desc">{desc}</span> : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function Pcs2SegmentedControl({ value, onChange, options, disabled, ariaLabel }) {
  return (
    <div className="pcs2-segmented" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={`pcs2-segmented-btn${active ? ' pcs2-segmented-btn--active' : ''}`}
            onClick={() => !disabled && onChange(opt.value)}
            disabled={disabled}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const OficinaConfig = ({ apiBaseUrl }) => {
  const [statusOficinaPause, setStatusOficinaPause] = useState('');
  const [visaoModo, setVisaoModo] = useState('modelo');
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();

  const loadConfig = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    try {
      setIsLoading(true);

      const [resPausa, resVisao] = await Promise.all([
        fetch(`${apiBaseUrl}/api/config/oficina-pausa`, { headers }),
        fetch(`${apiBaseUrl}/api/config/oficina-visao-os`, { headers }),
      ]);

      const [resultPausa, resultVisao] = await Promise.all([resPausa.json(), resVisao.json()]);

      let pause = '';
      let visao = 'modelo';

      if (resultPausa.success && resultPausa.data) {
        pause = String(resultPausa.data.codstatus || '').trim();
      } else if (!resultPausa.success) {
        toastError(resultPausa.message || 'Erro ao carregar configuração de pausa');
      }

      if (resultVisao.success && resultVisao.data) {
        const c = String(resultVisao.data.codstatus || '').trim();
        visao = c === '01' ? 'cliente' : 'modelo';
      } else if (!resultVisao.success) {
        toastError(resultVisao.message || 'Erro ao carregar visualização da fila');
      }

      setStatusOficinaPause(pause);
      setVisaoModo(visao);
      setSavedSnapshot({ pause, visao });
    } catch {
      toastError('Erro ao carregar configuração de Oficina');
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, toastError]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handlePauseChange = (e) => {
    const value = (e.target.value || '').toUpperCase();
    if (value.length <= 2) {
      setStatusOficinaPause(value);
    }
  };

  const isDirty =
    savedSnapshot !== null &&
    (statusOficinaPause !== savedSnapshot.pause || visaoModo !== savedSnapshot.visao);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const token = localStorage.getItem('authToken');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [resPausa, resVisao] = await Promise.all([
        fetch(`${apiBaseUrl}/api/config/oficina-pausa`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ statusOficinaPause: statusOficinaPause || '' }),
        }),
        fetch(`${apiBaseUrl}/api/config/oficina-visao-os`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ modo: visaoModo }),
        }),
      ]);

      const [resultPausa, resultVisao] = await Promise.all([resPausa.json(), resVisao.json()]);

      if (resultPausa.success && resultVisao.success) {
        setSavedSnapshot({ pause: statusOficinaPause, visao: visaoModo });
        toastSuccess('Configuração salva com sucesso!');
      } else {
        toastError(
          resultPausa.message || resultVisao.message || 'Erro ao salvar configuração'
        );
      }
    } catch {
      toastError('Erro ao salvar configuração de Oficina');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <ProcessConfigSkeleton variant="oficina" ariaLabel="Carregando configuração de oficina" />;
  }

  return (
    <div className="pcs2-page">
      <Pcs2Section
        icon={Route}
        title="Configuração de processos gerais"
        desc="Configuração de processos da oficina técnica"
      >
        <Pcs2Subblock
          title="Configure a pausa de ordens de serviço"
          desc="Durante o processo de revisão, quando houver pausas, defina o status em que a ordem de serviço passará a assumir nestas pausas"
        >
          <div className="pcs2-card">
            <div className="pcs2-list">
              <Pcs2ListItem
                id="status_oficina_pause"
                icon={IconClockPause}
                label="Status de pausa de OS"
                value={statusOficinaPause}
                onChange={handlePauseChange}
              />
            </div>
          </div>
        </Pcs2Subblock>

        <Pcs2Subblock hideHead>
          <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
            <Pcs2SettingRow
              title="Visualização de OS"
              desc="Qual informação o técnico verá em sua fila"
            >
              <Pcs2SegmentedControl
                ariaLabel="Visualização de OS na fila do técnico"
                value={visaoModo}
                onChange={setVisaoModo}
                options={[
                  { value: 'modelo', label: 'Modelo de equipamento' },
                  { value: 'cliente', label: 'Cliente' },
                ]}
              />
            </Pcs2SettingRow>
          </div>
        </Pcs2Subblock>
      </Pcs2Section>

      {Toaster}

      {createPortal(
        <button
          type="button"
          className="pcs2-save-fab"
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          aria-label="Salvar configuração"
        >
          {isSaving ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
          {isSaving ? 'Salvando...' : 'Salvar configuração'}
        </button>,
        document.body
      )}
    </div>
  );
};

export default OficinaConfig;
